import { motion } from "framer-motion";
import { ArrowRight, Play } from "lucide-react";
import HeroBackground from "@/components/sections/HeroBackground";
import { CALENDLY_URL } from "@/lib/config";

const fade = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-24 md:pb-32 min-h-[92vh] flex items-center"
    >
      <HeroBackground />

      <div className="max-w-7xl mx-auto px-6 md:px-10 w-full relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            {...fade}
            transition={{ duration: 0.6 }}
            data-testid="hero-badge"
            className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1.5 mono-label bg-background/60 backdrop-blur-sm"
          >
            <span className="accent-dot animate-pulse-glow" />
            <span className="text-muted-foreground">Built for modern engineering teams</span>
          </motion.div>

          <motion.h1
            {...fade}
            transition={{ duration: 0.7, delay: 0.08 }}
            data-testid="hero-heading"
            className="font-display font-semibold mt-6 text-balance tracking-[-0.035em] leading-[1.02] text-5xl sm:text-6xl md:text-7xl"
          >
            Meet your first{" "}
            <span className="relative inline-block">
              <span className="relative z-10">AI Product Quality Engineer</span>
              <span
                aria-hidden
                className="absolute left-0 right-0 bottom-1 h-3 md:h-4 -z-0 rounded-sm"
                style={{
                  background:
                    "linear-gradient(90deg, hsl(var(--accent) / 0.35), hsl(var(--accent) / 0.05))",
                }}
              />
            </span>
            <span className="text-muted-foreground">.</span>
          </motion.h1>

          <motion.p
            {...fade}
            transition={{ duration: 0.7, delay: 0.18 }}
            data-testid="hero-subheading"
            className="mt-7 text-base md:text-lg text-muted-foreground max-w-2xl mx-auto text-pretty leading-relaxed"
          >
            Liskov AI understands your frontend and backend codebase, thinks like a real
            customer, and performs deep exploratory testing across browsers, devices, and
            user journeys — before your users ever find the bugs.
          </motion.p>

          <motion.div
            {...fade}
            transition={{ duration: 0.7, delay: 0.28 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
          >
            <a
              data-testid="hero-cta-demo"
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary group"
            >
              Schedule Demo
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
            <a
              data-testid="hero-cta-vision"
              href="#vision"
              className="btn-secondary group"
            >
              <Play className="h-3.5 w-3.5 fill-current" />
              Watch Product Vision
            </a>
          </motion.div>

          <motion.div
            {...fade}
            transition={{ duration: 0.7, delay: 0.4 }}
            className="mt-16 flex items-center justify-center gap-6 text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground"
          >
            <span>Code-aware</span>
            <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
            <span>Exploratory</span>
            <span className="h-1 w-1 rounded-full bg-muted-foreground/40" />
            <span>Autonomous</span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
